import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  MessagesSquare,
  RefreshCw,
  AlertTriangle,
  Building2,
  Store,
  Euro,
  CalendarCheck,
  ChevronDown,
  Users,
  PlugZap,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Phone,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";

// ── Contrato de GET /admin/whatsapp-ai/overview?days= (backend ya desplegado,
// ver Documentos/PLAN_WHATSAPP_IA.md → "Endpoint de coste por cliente"). No
// tocar el shape sin avisar al agente backend.
interface OverviewLocation {
  id: string;
  name: string;
  phoneNumber: string | null;
  phoneNumberId: string | null;
}

interface OverviewConversations {
  total: number;
  activeInPeriod: number;
  human: number;
}

interface OverviewMessages {
  incoming: number;
  bot: number;
  staff: number;
  total: number;
}

interface OverviewTemplateCount {
  templateName: string;
  count: number;
}

interface OverviewTemplates {
  total: number;
  byTemplate: OverviewTemplateCount[];
}

interface OverviewUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  measuredMessages: number;
  unmeasuredMessages: number;
  estimatedCostEur: number;
}

interface OverviewRow {
  organizationId: string;
  organizationName: string;
  activeLocations: OverviewLocation[];
  conversations: OverviewConversations;
  messages: OverviewMessages;
  handoffs: number;
  reservations: number;
  templates: OverviewTemplates;
  usage: OverviewUsage;
}

interface OverviewTotals {
  organizations: number;
  activeLocationsCount: number;
  conversations: OverviewConversations;
  messages: OverviewMessages;
  handoffs: number;
  reservations: number;
  templates: OverviewTemplates;
  usage: OverviewUsage;
}

interface OverviewPricing {
  model: string;
  inputEurPerMillionTokens: number;
  outputEurPerMillionTokens: number;
  note: string;
}

interface OverviewData {
  days: number;
  since: string;
  generatedAt: string;
  degraded: boolean;
  pricing: OverviewPricing;
  rows: OverviewRow[];
  totals: OverviewTotals;
}

// ── Contrato de GET /admin/whatsapp-ai/provisioning y
// POST /admin/whatsapp-ai/provision/:locationId (backend ya desplegado, ver
// Documentos/PLAN_WHATSAPP_IA.md → "Endpoints de superadmin del
// aprovisionamiento"). `provisioningStatus` NO es un enum cerrado en el
// contrato: nunca hacer un switch sin default sobre su valor — pintar SIEMPRE
// `provisioningStatusLabel` (ya en español, la calcula el backend) y derivar
// el color del badge solo de `provisioningStatus === "registered" | "failed"`
// más `inProgress`, con un "gris + su etiqueta" de respaldo para todo lo
// demás (incluido `null` = nunca aprovisionado). Nunca pintar PIN ni código:
// el backend no los devuelve nunca a este panel (select dedicado que los
// excluye, ni siquiera al superadmin).
interface ProvisioningRow {
  organizationId: string;
  organizationName: string;
  locationId: string;
  locationName: string;
  locationActive: boolean;
  whatsappPhoneNumber: string | null;
  whatsappPhoneNumberId: string | null;
  provisioningStatus: string | null;
  provisioningStatusLabel: string;
  provisioningError: string | null;
  whatsappAiEnabled: boolean;
  addOnActive: boolean;
  inProgress: boolean;
  needsAttention: boolean;
  updatedAt: string;
}

interface ProvisioningTotals {
  locations: number;
  registered: number;
  inProgress: number;
  failed: number;
  notStarted: number;
  needsAttention: number;
}

interface ProvisioningData {
  generatedAt: string;
  degraded: boolean;
  rows: ProvisioningRow[];
  totals: ProvisioningTotals;
}

interface ProvisionActionData {
  locationId: string;
  locationName: string;
  status: string | null;
  statusLabel: string;
  completed: boolean;
  reason?: string;
}

interface ProvisionActionBody {
  success: boolean;
  message: string;
  data: ProvisionActionData;
}

const DAY_OPTIONS = [7, 30, 90] as const;
type DayOption = (typeof DAY_OPTIONS)[number];

/** Cada tick espera al listado antes de decidir si alguna fila sigue "en
 * marcha"; tope defensivo de ~5 min por sede (el orquestador sigue en
 * background aunque el panel deje de mirar). */
const PROVISIONING_POLL_INTERVAL_MS = 18_000;
const PROVISIONING_POLL_MAX_MS = 5 * 60 * 1000;

function formatEur(value: number): string {
  return `${value.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function formatNumber(value: number): string {
  return value.toLocaleString("es-ES");
}

function formatDateTime(value: string): string {
  try {
    return new Date(value).toLocaleString("es-ES");
  } catch {
    return value;
  }
}

/** Título "3 sedes: Kursaal, Melilla Centro, ..." para el `title` del badge. */
function locationsTooltip(locations: OverviewLocation[]): string {
  if (locations.length === 0) return "Sin sedes con el bot activo";
  return locations
    .map((loc) => (loc.phoneNumber ? `${loc.name} (${loc.phoneNumber})` : loc.name))
    .join(", ");
}

/** Estilo del badge de estado del aprovisionamiento. Puro, sin Prisma/switch
 * cerrado — cualquier estado que no reconozcamos cae en el gris por defecto,
 * pero SIEMPRE se pinta `provisioningStatusLabel` (nunca un texto inventado
 * aquí). */
function getProvisioningBadgeStyle(row: ProvisioningRow): {
  className: string;
  icon: typeof CheckCircle2;
  spin?: boolean;
} {
  if (row.provisioningStatus === "registered") {
    return { className: "bg-green-100 text-green-700", icon: CheckCircle2 };
  }
  if (row.provisioningStatus === "failed") {
    return { className: "bg-red-100 text-red-700", icon: XCircle };
  }
  if (row.inProgress) {
    return { className: "bg-blue-100 text-blue-700", icon: Loader2, spin: true };
  }
  return { className: "bg-gray-100 text-gray-500", icon: Clock };
}

function ProvisioningStatusBadge({ row }: { row: ProvisioningRow }) {
  const { className, icon: Icon, spin } = getProvisioningBadgeStyle(row);
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${className}`}
    >
      <Icon className={`w-3 h-3 ${spin ? "animate-spin" : ""}`} />
      {row.provisioningStatusLabel}
    </span>
  );
}

function TotalCard({
  label,
  value,
  valueClassName = "text-gray-900",
}: {
  label: string;
  value: number | undefined;
  valueClassName?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${valueClassName}`}>
        {value !== undefined ? formatNumber(value) : "—"}
      </p>
    </div>
  );
}

type PanelTab = "cost" | "provisioning";

export default function WhatsAppAi() {
  const [activeTab, setActiveTab] = useState<PanelTab>("cost");

  // ── Estado de la pestaña "Coste" ──────────────────────────────────────
  const [days, setDays] = useState<DayOption>(30);
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get("/admin/whatsapp-ai/overview", {
        params: { days },
      });
      setData(res.data.data as OverviewData);
    } catch (err) {
      setError(
        extractApiErrorMessage(
          err,
          "No se pudieron cargar las métricas de WhatsApp IA",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (activeTab === "cost") fetchOverview();
  }, [activeTab, fetchOverview]);

  const toggleExpanded = (orgId: string) => {
    setExpandedOrgId((current) => (current === orgId ? null : orgId));
  };

  const totals = data?.totals ?? null;
  const rows = data?.rows ?? [];

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Cabecera */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessagesSquare className="w-6 h-6 text-primary-600 flex-shrink-0" />
            WhatsApp IA · Panel Superadmin
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {activeTab === "cost"
              ? "Consumo y coste estimado en OpenAI por organización."
              : "Alta automática del número de WhatsApp por sede (Meta Cloud API)."}
            {activeTab === "cost" && data && (
              <span className="block sm:inline sm:ml-1 text-gray-400">
                Generado: {formatDateTime(data.generatedAt)}
              </span>
            )}
          </p>
        </div>
        {activeTab === "cost" && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`px-3 py-1.5 text-xs rounded-lg border ${
                    days === d
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Últimos {d}d
                </button>
              ))}
            </div>
            <button
              onClick={fetchOverview}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refrescar
            </button>
          </div>
        )}
      </div>

      {/* Pestañas */}
      <div className="flex gap-1 mb-4 bg-white rounded-lg p-1 shadow-sm border border-gray-200 w-fit">
        <button
          onClick={() => setActiveTab("cost")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "cost"
              ? "bg-primary-100 text-primary-700"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          <Euro className="w-4 h-4" />
          Coste
        </button>
        <button
          onClick={() => setActiveTab("provisioning")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "provisioning"
              ? "bg-primary-100 text-primary-700"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          <PlugZap className="w-4 h-4" />
          Aprovisionamiento
        </button>
      </div>

      {activeTab === "cost" && (
        <>
          {/* Banner de datos incompletos */}
          {data?.degraded && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 md:p-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-800">
                  Datos incompletos: alguna consulta falló
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Algunos números de este informe pueden estar por debajo de la
                  realidad. No los trates como definitivos hasta refrescar y
                  comprobar que el aviso desaparece.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 md:p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Tarjetas de totales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-xl border border-gray-200 p-3 min-w-0">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" />
                Organizaciones activas
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {totals ? formatNumber(totals.organizations) : "—"}
              </p>
              {totals && (
                <p className="text-xs text-gray-400 mt-1">
                  {formatNumber(totals.activeLocationsCount)} sedes con el bot
                </p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-3 min-w-0">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <MessagesSquare className="w-3.5 h-3.5" />
                Conversaciones del periodo
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {totals ? formatNumber(totals.conversations.activeInPeriod) : "—"}
              </p>
              {totals && (
                <p className="text-xs text-gray-400 mt-1">
                  {formatNumber(totals.conversations.total)} en total
                  {totals.conversations.human > 0 && (
                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                      {formatNumber(totals.conversations.human)} en humano ahora
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-3 min-w-0">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <CalendarCheck className="w-3.5 h-3.5" />
                Reservas del bot
              </p>
              <p className="text-2xl font-bold text-green-700">
                {totals ? formatNumber(totals.reservations) : "—"}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-3 min-w-0">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Euro className="w-3.5 h-3.5" />
                Coste total estimado
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {totals ? formatEur(totals.usage.estimatedCostEur) : "—"}
              </p>
              {totals && (
                <p className="text-xs text-gray-400 mt-1">
                  {formatNumber(totals.usage.measuredMessages)} de{" "}
                  {formatNumber(
                    totals.usage.measuredMessages + totals.usage.unmeasuredMessages,
                  )}{" "}
                  respuestas medidas
                  {totals.usage.unmeasuredMessages > 0 && (
                    <span className="ml-1 text-amber-600" title="Hay respuestas del bot sin tokens registrados: el importe es parcial">
                      ⚠️ parcial
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Contenido */}
          {loading && !data ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
              Cargando...
            </div>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
              No hay organizaciones con actividad de WhatsApp IA en este periodo.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Vista móvil: cards */}
              <div className="md:hidden divide-y divide-gray-100">
                {rows.map((row) => {
                  const isExpanded = expandedOrgId === row.organizationId;
                  return (
                    <div key={row.organizationId} className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 text-sm truncate">
                            {row.organizationName}
                          </p>
                          <p
                            className="text-xs text-gray-500 truncate flex items-center gap-1"
                            title={locationsTooltip(row.activeLocations)}
                          >
                            <Store className="w-3 h-3 flex-shrink-0" />
                            {row.activeLocations.length} sede
                            {row.activeLocations.length === 1 ? "" : "s"} activa
                            {row.activeLocations.length === 1 ? "" : "s"}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-gray-900">
                            {formatEur(row.usage.estimatedCostEur)}
                          </p>
                          <p
                            className="text-[10px] text-gray-400"
                            title="Respuestas del bot con tokens registrados / total"
                          >
                            {formatNumber(row.usage.measuredMessages)}/
                            {formatNumber(
                              row.usage.measuredMessages + row.usage.unmeasuredMessages,
                            )}{" "}
                            medidas
                            {row.usage.unmeasuredMessages > 0 && " ⚠️"}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-2">
                        <div>
                          <span className="text-gray-400">Conversaciones: </span>
                          {formatNumber(row.conversations.activeInPeriod)}
                          {" / "}
                          {formatNumber(row.conversations.total)}
                          {row.conversations.human > 0 && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                              {row.conversations.human} humano
                            </span>
                          )}
                        </div>
                        <div>
                          <span className="text-gray-400">Handoffs: </span>
                          {formatNumber(row.handoffs)}
                        </div>
                        <div>
                          <span className="text-gray-400">Mensajes: </span>
                          {row.messages.incoming} in · {row.messages.bot} bot ·{" "}
                          {row.messages.staff} staff
                        </div>
                        <div>
                          <span className="text-gray-400">Reservas: </span>
                          <span className="text-green-700 font-medium">
                            {formatNumber(row.reservations)}
                          </span>
                        </div>
                      </div>

                      {row.templates.total > 0 ? (
                        <button
                          onClick={() => toggleExpanded(row.organizationId)}
                          className="w-full flex items-center justify-between text-xs text-gray-600 border-t border-gray-100 pt-2"
                        >
                          <span>
                            {formatNumber(row.templates.total)} plantillas enviadas
                          </span>
                          <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      ) : (
                        <p className="text-xs text-gray-400 border-t border-gray-100 pt-2">
                          Sin plantillas enviadas en el periodo
                        </p>
                      )}
                      {isExpanded && row.templates.byTemplate.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs text-gray-600 bg-gray-50 rounded-lg p-2">
                          {row.templates.byTemplate.map((t) => (
                            <li
                              key={t.templateName}
                              className="flex items-center justify-between gap-2"
                            >
                              <span className="truncate">{t.templateName}</span>
                              <span className="font-medium text-gray-900">
                                {formatNumber(t.count)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Vista escritorio: tabla */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Organización
                      </th>
                      <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Sedes activas
                      </th>
                      <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Conversaciones
                      </th>
                      <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Mensajes
                      </th>
                      <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Handoffs
                      </th>
                      <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Reservas
                      </th>
                      <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Plantillas
                      </th>
                      <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Coste est. €
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => {
                      const isExpanded = expandedOrgId === row.organizationId;
                      const canExpandTemplates = row.templates.byTemplate.length > 0;
                      return (
                        <Fragment key={row.organizationId}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 max-w-[220px]">
                              <p className="font-medium text-gray-900 truncate">
                                {row.organizationName}
                              </p>
                            </td>
                            <td className="px-3 py-3">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  row.activeLocations.length > 0
                                    ? "bg-blue-50 text-blue-700"
                                    : "bg-gray-100 text-gray-500"
                                }`}
                                title={locationsTooltip(row.activeLocations)}
                              >
                                <Store className="w-3 h-3" />
                                {row.activeLocations.length}
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-gray-900">
                                {formatNumber(row.conversations.activeInPeriod)}
                                <span className="text-gray-400">
                                  {" "}
                                  / {formatNumber(row.conversations.total)}
                                </span>
                              </div>
                              {row.conversations.human > 0 && (
                                <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                                  <Users className="w-3 h-3" />
                                  {row.conversations.human} en humano ahora
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-gray-700 whitespace-nowrap">
                              <span title="Entrantes del cliente">
                                {row.messages.incoming} in
                              </span>
                              {" · "}
                              <span title="Generados por el bot">
                                {row.messages.bot} bot
                              </span>
                              {" · "}
                              <span title="Escritos por el equipo">
                                {row.messages.staff} staff
                              </span>
                            </td>
                            <td className="px-3 py-3 text-gray-700">
                              {formatNumber(row.handoffs)}
                            </td>
                            <td className="px-3 py-3 text-green-700 font-medium">
                              {formatNumber(row.reservations)}
                            </td>
                            <td className="px-3 py-3">
                              {canExpandTemplates ? (
                                <button
                                  onClick={() => toggleExpanded(row.organizationId)}
                                  className="inline-flex items-center gap-1 text-gray-700 hover:text-primary-600"
                                >
                                  {formatNumber(row.templates.total)}
                                  <ChevronDown
                                    className={`w-3.5 h-3.5 transition-transform ${
                                      isExpanded ? "rotate-180" : ""
                                    }`}
                                  />
                                </button>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <p className="font-bold text-gray-900">
                                {formatEur(row.usage.estimatedCostEur)}
                              </p>
                              <p
                                className="text-[11px] text-gray-400"
                                title="Respuestas del bot con tokens registrados / total"
                              >
                                {formatNumber(row.usage.measuredMessages)} de{" "}
                                {formatNumber(
                                  row.usage.measuredMessages +
                                    row.usage.unmeasuredMessages,
                                )}{" "}
                                medidas
                                {row.usage.unmeasuredMessages > 0 && (
                                  <span
                                    className="ml-1 text-amber-600"
                                    title="Hay respuestas del bot sin tokens registrados: el importe es parcial"
                                  >
                                    ⚠️ parcial
                                  </span>
                                )}
                              </p>
                            </td>
                          </tr>
                          {isExpanded && canExpandTemplates && (
                            <tr className="bg-gray-50">
                              <td colSpan={8} className="px-4 py-3">
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1.5">
                                  Desglose de plantillas — {row.organizationName}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {row.templates.byTemplate.map((t) => (
                                    <span
                                      key={t.templateName}
                                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white border border-gray-200 text-xs text-gray-700"
                                    >
                                      {t.templateName}
                                      <span className="font-semibold text-gray-900">
                                        {formatNumber(t.count)}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Nota de precios — estimación honesta, no la escondemos */}
          {data && (
            <div className="mt-4 text-xs text-gray-500 bg-white border border-gray-200 rounded-xl p-3 md:p-4">
              <p className="text-gray-700">
                <span className="font-semibold">Modelo:</span> {data.pricing.model}{" "}
                · {data.pricing.inputEurPerMillionTokens.toLocaleString("es-ES")}{" "}
                €/M tokens entrada ·{" "}
                {data.pricing.outputEurPerMillionTokens.toLocaleString("es-ES")}{" "}
                €/M tokens salida
              </p>
              <p className="mt-1">{data.pricing.note}</p>
            </div>
          )}
        </>
      )}

      {activeTab === "provisioning" && <ProvisioningPanel />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Pestaña "Aprovisionamiento"
// ─────────────────────────────────────────────────────────────────────────

function ProvisioningPanel() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  const [data, setData] = useState<ProvisioningData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLocationId, setExpandedLocationId] = useState<string | null>(null);
  const [actioningIds, setActioningIds] = useState<Set<string>>(new Set());
  const [isPolling, setIsPolling] = useState(false);

  // `locationId → cuándo empezó a esperarse un 202` — vive fuera del estado
  // porque solo lo lee el propio intervalo de sondeo, no el render.
  const pollStartRef = useRef<Map<string, number>>(new Map());
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchProvisioning = useCallback(async (): Promise<ProvisioningData | null> => {
    if (mountedRef.current) setLoading(true);
    try {
      const res = await apiClient.get("/admin/whatsapp-ai/provisioning");
      const payload = res.data.data as ProvisioningData;
      if (mountedRef.current) {
        setData(payload);
        setError(null);
      }
      return payload;
    } catch (err) {
      if (mountedRef.current) {
        setError(
          extractApiErrorMessage(err, "No se pudo cargar el aprovisionamiento"),
        );
      }
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProvisioning();
  }, [fetchProvisioning]);

  // Sondeo mientras algún aprovisionamiento lanzado desde este panel siga
  // "en curso" (respuesta 202): cada ~18s hasta que la fila salga de
  // `inProgress` o pase el tope de ~5 min (el orquestador sigue corriendo en
  // background aunque el panel deje de mirar).
  useEffect(() => {
    if (!isPolling) return;
    const interval = setInterval(async () => {
      const payload = await fetchProvisioning();
      const rows = payload?.rows ?? [];
      const now = Date.now();
      for (const [locationId, startedAt] of pollStartRef.current) {
        const row = rows.find((r) => r.locationId === locationId);
        const expired = now - startedAt > PROVISIONING_POLL_MAX_MS;
        if (!row?.inProgress || expired) {
          pollStartRef.current.delete(locationId);
        }
      }
      if (pollStartRef.current.size === 0 && mountedRef.current) {
        setIsPolling(false);
      }
    }, PROVISIONING_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isPolling, fetchProvisioning]);

  const toggleError = (locationId: string) => {
    setExpandedLocationId((current) => (current === locationId ? null : locationId));
  };

  const handleProvision = async (row: ProvisioningRow) => {
    const isRetry = row.provisioningStatus === "failed";
    const confirmed = await confirm({
      title: isRetry ? "Reintentar aprovisionamiento" : "Aprovisionar WhatsApp IA",
      message: row.whatsappPhoneNumber
        ? `Se relanzará el alta del número ${row.whatsappPhoneNumber} en Meta para «${row.locationName}» (${row.organizationName}). Puede tardar varios minutos.`
        : `«${row.locationName}» (${row.organizationName}) no tiene número: se COMPRARÁ un número de WhatsApp real (gasto real en Twilio) y se dará de alta en Meta. Puede tardar varios minutos.`,
      confirmText: isRetry ? "Reintentar" : "Aprovisionar",
      cancelText: "Cancelar",
      danger: !row.whatsappPhoneNumber,
    });
    if (!confirmed) return;

    setActioningIds((prev) => new Set(prev).add(row.locationId));
    try {
      // Timeout largo SOLO en esta llamada: el orquestador puede tardar hasta
      // ~75s en responder antes de degradar a 202.
      const res = await apiClient.post<ProvisionActionBody>(
        `/admin/whatsapp-ai/provision/${row.locationId}`,
        undefined,
        { timeout: 90_000 },
      );
      if (res.status === 202) {
        showToast(
          res.data.message ||
            "El aprovisionamiento sigue en marcha. Comprobando el estado cada poco…",
          "info",
        );
        pollStartRef.current.set(row.locationId, Date.now());
        setIsPolling(true);
      } else {
        showToast(
          res.data.message || "WhatsApp IA aprovisionado correctamente.",
          "success",
        );
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const fallback =
        status === 409
          ? "Ya hay un aprovisionamiento en curso para esta sede."
          : status === 429
            ? "Demasiados intentos seguidos. Espera un rato antes de reintentar."
            : "No se pudo aprovisionar WhatsApp IA.";
      showToast(extractApiErrorMessage(err, fallback), "error");
    } finally {
      setActioningIds((prev) => {
        const next = new Set(prev);
        next.delete(row.locationId);
        return next;
      });
      // Refresca siempre: en 200 confirma el nuevo estado, en 202/409 refleja
      // que la sede está realmente `inProgress` (deshabilita el botón), y en
      // 429/502 trae el `provisioningError` recién escrito para "Reintentar".
      fetchProvisioning();
    }
  };

  const totals = data?.totals ?? null;
  const rows = data?.rows ?? [];

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-gray-400">
          {data && `Generado: ${formatDateTime(data.generatedAt)}`}
          {isPolling && (
            <span className="ml-2 inline-flex items-center gap-1 text-blue-600">
              <Loader2 className="w-3 h-3 animate-spin" />
              comprobando aprovisionamientos en marcha…
            </span>
          )}
        </p>
        <button
          onClick={() => fetchProvisioning()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refrescar
        </button>
      </div>

      {data?.degraded && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 md:p-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              Datos incompletos: alguna consulta falló
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              La lista puede estar incompleta o desactualizada. No la trates
              como definitiva hasta refrescar y comprobar que el aviso
              desaparece.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 md:p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Mini-tarjetas de totales */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-4">
        <TotalCard label="Sedes" value={totals?.locations} />
        <TotalCard
          label="Registradas"
          value={totals?.registered}
          valueClassName="text-green-700"
        />
        <TotalCard
          label="En curso"
          value={totals?.inProgress}
          valueClassName="text-blue-700"
        />
        <TotalCard
          label="Fallidas"
          value={totals?.failed}
          valueClassName="text-red-700"
        />
        <TotalCard
          label="Sin aprovisionar"
          value={totals?.notStarted}
          valueClassName="text-gray-500"
        />
        <TotalCard
          label="Necesitan atención"
          value={totals?.needsAttention}
          valueClassName="text-amber-700"
        />
      </div>

      {loading && !data ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          Cargando...
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          No hay sedes que aprovisionar: ninguna tiene el bot activo, huella
          previa del aprovisionamiento ni el add-on de WhatsApp IA contratado.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Vista móvil: cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {rows.map((row) => {
              const isActioning = actioningIds.has(row.locationId);
              const isErrorExpanded = expandedLocationId === row.locationId;
              return (
                <div
                  key={row.locationId}
                  className={`p-4 ${row.locationActive ? "" : "opacity-60"}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {row.locationName}
                        {!row.locationActive && (
                          <span className="ml-1.5 text-[10px] font-normal text-gray-400">
                            (inactiva)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                        <Building2 className="w-3 h-3 flex-shrink-0" />
                        {row.organizationName}
                      </p>
                    </div>
                    <ProvisioningStatusBadge row={row} />
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                    <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    {row.whatsappPhoneNumber || (
                      <span className="text-gray-400 italic">Sin número</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                        row.addOnActive
                          ? "bg-green-50 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {row.addOnActive ? "Add-on activo" : "Sin add-on"}
                    </span>
                    {row.needsAttention && row.provisioningStatus !== "failed" && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3" />
                        Paga y no tiene número
                      </span>
                    )}
                  </div>

                  {row.provisioningError && (
                    <button
                      onClick={() => toggleError(row.locationId)}
                      className="w-full flex items-center justify-between text-xs text-red-600 border-t border-gray-100 pt-2 mb-2"
                    >
                      <span className="truncate">Ver motivo del fallo</span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${
                          isErrorExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  )}
                  {isErrorExpanded && row.provisioningError && (
                    <p className="text-xs text-red-700 bg-red-50 rounded-lg p-2 mb-2 break-words">
                      {row.provisioningError}
                    </p>
                  )}

                  {row.provisioningStatus !== "registered" && (
                    <button
                      onClick={() => handleProvision(row)}
                      disabled={row.inProgress || isActioning}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isActioning || row.inProgress ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <PlugZap className="w-3.5 h-3.5" />
                      )}
                      {isActioning
                        ? "Aprovisionando…"
                        : row.inProgress
                          ? "En curso…"
                          : row.provisioningStatus === "failed"
                            ? "Reintentar"
                            : "Aprovisionar"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Vista escritorio: tabla */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Organización / Sede
                  </th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Número
                  </th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Add-on
                  </th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Estado
                  </th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Actualizado
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const isActioning = actioningIds.has(row.locationId);
                  const isErrorExpanded = expandedLocationId === row.locationId;
                  return (
                    <Fragment key={row.locationId}>
                      <tr
                        className={`hover:bg-gray-50 ${
                          row.locationActive ? "" : "opacity-60"
                        }`}
                      >
                        <td className="px-4 py-3 max-w-[260px]">
                          <p className="font-medium text-gray-900 truncate">
                            {row.locationName}
                            {!row.locationActive && (
                              <span className="ml-1.5 text-[10px] font-normal text-gray-400">
                                (inactiva)
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {row.organizationName}
                          </p>
                        </td>
                        <td className="px-3 py-3 text-gray-700 whitespace-nowrap">
                          {row.whatsappPhoneNumber || (
                            <span className="text-gray-400 italic">Sin número</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                              row.addOnActive
                                ? "bg-green-50 text-green-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {row.addOnActive ? "Activo" : "Sin add-on"}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <ProvisioningStatusBadge row={row} />
                            {row.needsAttention && row.provisioningStatus !== "failed" && (
                              <AlertTriangle
                                className="w-3.5 h-3.5 text-amber-500"
                                aria-label="Paga el add-on y no tiene número"
                              />
                            )}
                          </div>
                          {row.provisioningError && (
                            <button
                              onClick={() => toggleError(row.locationId)}
                              className="mt-1 inline-flex items-center gap-1 text-[11px] text-red-600 hover:underline"
                            >
                              Ver motivo
                              <ChevronDown
                                className={`w-3 h-3 transition-transform ${
                                  isErrorExpanded ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-3 text-gray-500 text-xs whitespace-nowrap">
                          {formatDateTime(row.updatedAt)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {row.provisioningStatus !== "registered" ? (
                            <button
                              onClick={() => handleProvision(row)}
                              disabled={row.inProgress || isActioning}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isActioning || row.inProgress ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <PlugZap className="w-3.5 h-3.5" />
                              )}
                              {isActioning
                                ? "Aprovisionando…"
                                : row.inProgress
                                  ? "En curso…"
                                  : row.provisioningStatus === "failed"
                                    ? "Reintentar"
                                    : "Aprovisionar"}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                      {isErrorExpanded && row.provisioningError && (
                        <tr className="bg-red-50">
                          <td colSpan={6} className="px-4 py-3">
                            <p className="text-xs font-semibold text-red-700 uppercase mb-1">
                              Último error — {row.locationName}
                            </p>
                            <p className="text-xs text-red-700 break-words">
                              {row.provisioningError}
                            </p>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
